import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Loader2, Check, Camera, X, ZoomIn } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('crm_token')}`,
});

const FIELD_GROUPS = [
  { title: null, fields: [
    ['channelPartnerId', 'Channel Partner ID'],
    ['name', 'Name'],
    ['mobile1', 'Mobile No. 1'],
    ['mobile2', 'Mobile No. 2'],
    ['birthDate', 'Birth Date'],
    ['aadharNo', 'Aadhar No'],
    ['panNo', 'PAN No'],
    ['address', 'Address'],
    ['mailId', 'Mail ID'],
    ['gstNo', 'GST No'],
    ['reraNo', 'RERA No'],
    ['areaSpecialization', 'Area & Specialization'],
  ]},
  { title: 'Bank Details', fields: [
    ['bankAccountHolder', 'Name of A/C Holder'],
    ['bankName', 'Name of Bank'],
    ['accountNumber', 'Account Number'],
    ['ifscCode', 'IFSC Code'],
    ['branch', 'Branch'],
    ['dmName', 'D.M. Name'],
    ['admName', 'A.D.M Name'],
  ]},
];

// Validation rules keyed by form field
const VALIDATORS = {
  aadharNo: { pattern: /^\d{12}$/, message: 'Aadhar must be exactly 12 digits' },
  panNo: { pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, message: 'PAN format: ABCDE1234F (5 letters, 4 digits, 1 letter)' },
};

const InformationForm = ({ empId = null }) => {
  const [form, setForm] = useState(null);
  const [photoUrl, setPhotoUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState('idle');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState(false);
  const [showPhotoZoom, setShowPhotoZoom] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const saveTimer = useRef(null);
  const photoInputRef = useRef(null);

  const fetchForm = useCallback(async () => {
    setLoading(true);
    try {
      const q = empId ? `?emp_id=${encodeURIComponent(empId)}` : '';
      const res = await fetch(`${API}/profile-form${q}`, { headers: getH() });
      const data = await res.json();
      setForm(data.form);
      setPhotoUrl(data.photoUrl || '');
      setPhotoError(false);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [empId]);

  useEffect(() => { fetchForm(); }, [fetchForm]);

  const scheduleSave = useCallback((next) => {
    setSaveState('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/profile-form`, {
          method: 'PUT', headers: getH(),
          body: JSON.stringify(empId ? { ...next, emp_id: empId } : next),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || 'Save failed');
        }
        setSaveState('saved');
        setTimeout(() => setSaveState('idle'), 1500);
      } catch (e) {
        setSaveState('idle');
        toast.error(e.message || 'Could not save Information Form');
      }
    }, 800);
  }, [empId]);

  const handleChange = (key, value) => {
    // Inline validation
    if (VALIDATORS[key]) {
      const { pattern, message } = VALIDATORS[key];
      const trimmed = value.trim();
      if (trimmed && !pattern.test(trimmed)) {
        setFieldErrors(prev => ({ ...prev, [key]: message }));
      } else {
        setFieldErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
      }
    }
    setForm(prev => {
      const next = { ...prev, [key]: value };
      scheduleSave(next);
      return next;
    });
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (empId) fd.append('emp_id', empId);
      const res = await fetch(`${API}/profile-form/photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Photo upload failed');
      if (data.photoUrl) { setPhotoUrl(data.photoUrl); setPhotoError(false); }
    } catch (e) {
      toast.error(e.message || 'Could not upload photo');
    }
    setUploadingPhoto(false);
  };

  if (loading || !form) {
    return (
      <div className="flex items-center justify-center py-10 text-stone-400" data-testid="information-form-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading Information Form…
      </div>
    );
  }

  return (
    <div data-testid="information-form" className="rounded-2xl border border-stone-200 bg-white p-6 md:p-8 shadow-sm">
      <div className="flex items-start justify-between mb-6 border-b border-stone-100 pb-4">
        <div>
          <p className="text-xs font-semibold text-amber-600 tracking-wide uppercase">DBK Group Of Industries</p>
          <h2 className="text-xl font-bold text-stone-800 underline">Information Form</h2>
        </div>
        <div className="flex flex-col items-center gap-2">
          <div
            onClick={() => {
              if (photoUrl && !photoError) setShowPhotoZoom(true);
              else photoInputRef.current?.click();
            }}
            data-testid="information-form-photo-box"
            className="w-24 h-28 rounded-md border border-stone-300 bg-stone-50 flex items-center justify-center overflow-hidden cursor-pointer relative group"
            title={photoUrl && !photoError ? 'Click to view full size' : 'Click to upload photo'}
          >
            {uploadingPhoto ? (
              <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
            ) : photoUrl && !photoError ? (
              <>
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${photoUrl}`}
                  alt="Profile"
                  className="w-full h-full object-cover group-hover:opacity-70 transition-opacity"
                  data-testid="information-form-photo"
                  onError={() => setPhotoError(true)}
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <ZoomIn className="w-5 h-5 text-white drop-shadow-lg" />
                </div>
              </>
            ) : (
              <Camera className="w-6 h-6 text-stone-300 group-hover:text-stone-400" data-testid="information-form-photo-placeholder" />
            )}
          </div>
          <input ref={photoInputRef} type="file" accept="image/*" className="hidden"
            data-testid="information-form-photo-input" onChange={handlePhotoChange} />
          <button onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto}
            data-testid="information-form-photo-upload-btn"
            className="text-xs font-medium text-[#1F3864] hover:underline disabled:opacity-40">
            {uploadingPhoto ? 'Uploading…' : (photoUrl ? 'Change Photo' : 'Upload Photo')}
          </button>
        </div>
      </div>

      {FIELD_GROUPS.map((group, gi) => (
        <div key={gi} className={gi > 0 ? 'mt-6 pt-5 border-t border-stone-100' : ''}>
          {group.title && <h3 className="text-sm font-bold text-stone-600 uppercase tracking-wide mb-3">{group.title}</h3>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {group.fields.map(([key, label]) => (
              <div key={key}>
                <Label className="text-xs text-stone-500">{label}</Label>
                <Input
                  data-testid={`information-form-${key}`}
                  value={form[key] || ''}
                  onChange={(e) => handleChange(key, e.target.value)}
                  className={`mt-1 ${fieldErrors[key] ? 'border-red-400 focus-visible:ring-red-300' : ''}`}
                />
                {fieldErrors[key] && (
                  <p className="text-[10px] text-red-500 mt-0.5" data-testid={`information-form-error-${key}`}>{fieldErrors[key]}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="mt-5 text-xs text-stone-400 flex items-center gap-1.5" data-testid="information-form-save-state">
        {saveState === 'saving' && <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>}
        {saveState === 'saved' && <><Check className="w-3.5 h-3.5 text-emerald-600" /> Saved</>}
      </div>

      {/* ── Photo Zoom Lightbox ── */}
      {showPhotoZoom && photoUrl && !photoError && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setShowPhotoZoom(false)}
          data-testid="information-form-photo-zoom-overlay"
        >
          <div className="relative max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${photoUrl}`}
              alt="Profile Full Size"
              className="w-full rounded-xl shadow-2xl object-contain max-h-[80vh]"
              data-testid="information-form-photo-zoom-img"
              onError={() => { setPhotoError(true); setShowPhotoZoom(false); }}
            />
            <button
              onClick={() => setShowPhotoZoom(false)}
              data-testid="information-form-photo-zoom-close"
              className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-stone-100 transition-colors"
            >
              <X className="w-4 h-4 text-stone-700" />
            </button>
            <button
              onClick={() => { setShowPhotoZoom(false); photoInputRef.current?.click(); }}
              data-testid="information-form-photo-zoom-change-btn"
              className="mt-3 w-full py-2 rounded-lg bg-[#1F3864] text-white text-sm font-semibold hover:bg-[#162a4e] transition-colors"
            >
              Change Photo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InformationForm;
